/* import __COLOCATED_TEMPLATE__ from './lsg-select-chips-basic.hbs'; */
import $ from 'jquery';
import { scheduleOnce, debounce } from '@ember/runloop';
import { equal } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { A } from '@ember/array';
import Component from '@ember/component';
import {
  KEY_CODES,
  KEY_CODES__ARROWS_UP_AND_DOWN
} from "../../constants/ui";
import { stopClickEvent } from "../../utils/events";

/**
 * These are the different styles of the dropdown.
 *
 * @type {Object}
 */
export const LSG_SELECT_CHIPS_BASIC__SELECT_TYPES = {
  CHIP: 'chip', // multiple select with 'chips'
  TEXT: 'text' // just a single text entry allowed.
};

/**
 * @type {string}     This will signify that we have a grouped option heading in our flattened list of options.
 */
const GROUPED_OPTION_VALUE_SENTINEL = 'GROUPED_';

/**
 * This is a dropdown selector that populates with chips on selection.  Built with an Ember dropdown instead of
 * the other materialize version.
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'div',
  classNames: ['LSG-c-select-chips-basic'],
  classNameBindings: [
    '_isChipContainerActive:LSG-c-select-chips-basic--container-active',
    '_isOptionTypeText:LSG-c-select-chips-basic--option-type-text',
    'groupedOptions.length:LSG-c-select-chips-basic--has-grouped-options'
  ],

  /**
   * Register services
   */

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /**
   * This is the list of selectedOptions and is intended to be passed by reference.
   *
   * @type {Ember.A<Object>}
   */
  selectedOptions: A(),

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /**
   * This is a default option that when selected, will clear any of the other selections and go back to original
   * state with the placeholder showing.
   *
   * @see options     For the format of the defaultOption
   * @param {Object}
   */
  defaultOption: null,

  /**
   * @param {Boolean}      True if we only allow a single option to be selected.
   */
  isOnlySingleSelectionAllowed: false,

  /**
   * A placeholder that will be shown when we have no options selected yet.
   *
   * @param {String=}
   */
  placeholder: 'Select ...',

  /**
   * Callback function for clearing out selected
   *
   * @param {Function=}
   */
  onOptionsCleared: function() {},

  /**
   * Callback function when user selects an option
   *
   * @param {Function=}
   */
  onOptionSelected: function(optionSelected) {},

  /**
   * Callback function when user removes an option
   *
   * @param {Function=}
   */
  onOptionRemoved: function(optionRemoved) {},

  /**
   * options or groupedOptions are required
   *
   * With this component, instead of specifying the <option> elements within
   * the template, you may pass an array of objects to populate the
   * options.  Should be of the form:
   *
   * options = [
   *  {
   *    title: 'Option 1',
   *    value: 1,
   *    tooltip: Optional. text to show on hover of the chip.
   *  },
   *    ...
   * ];
   *
   * @property {array}
   */
  options: A(),

  /**
   * options or groupedOptions are required
   *
   * groupedOptions = [{
   *  groupName: 'regions',
   *  groupTitle: 'Regions of Justice',
   *  options: [{  ***** 'options' Must be a Ember.MutableArray *******
   *    title: 'Europe',
   *    value: '3',
   *    tooltip: Optional. text to show on hover of chip
   *  }, ...]
   *},{
   *  groupName: 'countries',
   *  groupTitle: 'Countries of Yore',
   *  options: [{ ***** 'options' Must be a Ember.MutableArray *******
   *    ...
   *  }, ...]
   *},
   *  ...
   *]
   *
   * @param {Ember.MutableArray}    See format above
   */
  groupedOptions: A(),

  /**
   * The style of the selected options.
   *
   * @param {String}  LSG_SELECT_CHIPS_BASIC__SELECT_TYPES.*
   */
  selectedOptionType: LSG_SELECT_CHIPS_BASIC__SELECT_TYPES.CHIP,

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * A specific option has been selected, so update the state.
   *
   * @param {Dropdown} dropdown
   * @param {Object} optionSelected
   * @private
   */
  _addSelectedOption(dropdown, optionSelected) {
    if (this.isOnlySingleSelectionAllowed) {
      this.onOptionsCleared();
    }

    dropdown.actions.close();
    this.set('_inputTextValueHidden', '');

    this.groupedOptions.forEach(groupedOption => {
      groupedOption.options.forEach(option => {
        if (option.title === optionSelected.title && option.value === optionSelected.value) {
          optionSelected = Object.assign({}, optionSelected, {
            groupName: groupedOption.groupName
          });
        }
      });
    });

    this.onOptionSelected(optionSelected);
    this.set('_arrowKeySelectedIndex', -1);
  },

  /**
   * @type {Number}     This is the selected index if the arrow key was used to selected an option.
   */
  _arrowKeySelectedIndex: -1,

  /**
   * Function to clear the hidden input text.
   */
  _clearHiddenTextValueInput() {
    this.set('_inputTextValueHidden', '');
  },

  /**
   * This is a flattened array of options.  So, in the case of grouped options we will have a single array
   * of all options with the grouped option headings being specified by a value of the GROUPED_OPTION_VALUE_SENTINEL
   * as the first part of the option.
   *
   * @type {Ember.A<Object>}
   */
  _flattenedOptions: computed('defaultOption', 'options.[]', 'groupedOptions.options.[]', function() {
    const defaultOption = this.defaultOption;
    const allOptions = A();

    if (this.options.length) {
      this.options.forEach(option => allOptions.pushObject(option));
      return allOptions;
    }

    if (!this.groupedOptions.length) {
        return allOptions;
      }

    if (defaultOption) {
      allOptions.pushObject(defaultOption);
    }

    this.groupedOptions.forEach(groupedOption => {
      groupedOption.value = `${GROUPED_OPTION_VALUE_SENTINEL}${groupedOption.groupName}`;
      allOptions.pushObject(groupedOption);
      groupedOption.options.forEach(option => allOptions.pushObject(option));
    });

    return allOptions;
  }),

  /**
   * This is the input text as it is entered by the user.
   *
   * @type {String}
   */
  _inputTextValue: '',

  /**
   * This is the hidden input text, so we will continuously create a string from the _inputTextValue
   * and the user would not see it.  This will essentially mimic what the option element does for
   * searching for text in the dropdown.
   *
   * @type {String}
   */
  _inputTextValueHidden: '',

  /**
   * @return {Boolean}  True when we have at least one option that the user has selected.
   */
  _isChipContainerActive: computed('selectedOptions.length', function() {
    return this.get('selectedOptions.length') > 0;
  }),

  /**
   * @type {Boolean}
   */
  _isOptionTypeText: equal('selectedOptionType', LSG_SELECT_CHIPS_BASIC__SELECT_TYPES.TEXT),

  /**
   * Called after user changes the text in the hidden input element.
   */
  _inputTextHandler: function() {
    let hiddenTextValue = this.get('_inputTextValueHidden'),
      textValue = this.get('_inputTextValue');

    if (!textValue) {
      return;
    }

    hiddenTextValue += textValue;
    this.set('_inputTextValueHidden', hiddenTextValue);

    scheduleOnce('afterRender', this, function() {
      // always clear the input text value so the user won't see what they are typing.
      this.set('_inputTextValue', '');
    });

    debounce(this, this._clearHiddenTextValueInput, 1000);
    this._selectItemByText();
  },

  /**
   * Select an item in the dropdown menu by the text the user has entered.
   */
  _selectItemByText() {
    const options = this._flattenedOptions;
    const hiddenText = this._inputTextValueHidden;
    const foundOption = options.find(option => {
        const regex = new RegExp(`^${hiddenText}`, "i");
        return regex.test(option.title);
      });

    this.set('_arrowKeySelectedIndex', options.indexOf(foundOption));
    this.handleScrollSelectionIntoView();
  },

  handleArrowNavigation: action(function(key) {
    const currentlySelectedArrowIndex = this._arrowKeySelectedIndex;
    const options = this._flattenedOptions;
    const optionsLength = options.length;
    let nextArrowIndex = Math.min(currentlySelectedArrowIndex + 1, optionsLength - 1);

    if (key === KEY_CODES.ARROW_UP) {
      nextArrowIndex = Math.max(currentlySelectedArrowIndex - 1, 0);
    }

    const nextSelectedOption = options.objectAt(nextArrowIndex);
    const groupedRegex = new RegExp(`^${GROUPED_OPTION_VALUE_SENTINEL}`);
    if (groupedRegex.test(nextSelectedOption.value)) {
      nextArrowIndex = Math.min(currentlySelectedArrowIndex + 2, optionsLength - 1);

      if (key === KEY_CODES.ARROW_UP) {
        nextArrowIndex = Math.max(currentlySelectedArrowIndex - 2, 0);
      }
    }

    this.set('_arrowKeySelectedIndex', nextArrowIndex);
    this.handleScrollSelectionIntoView();
  }),

  handleScrollSelectionIntoView: action(function() {
    const parentScrollEl = $(this.element).find('.ember-basic-dropdown-content');
    const scrollEl = $(this.element).find('.LSG-c-select-chips-basic--options-list');
    const parentScrollElHeight = parentScrollEl.height();
    const optionHeight = scrollEl.find('.LSG-c-select-chips-basic--options-list--item').height();
    const arrowSelectedIndex = this._arrowKeySelectedIndex;
    const arrowLocationPixels = arrowSelectedIndex * optionHeight;

    if (!parentScrollEl.offset() || !scrollEl.offset()) {
      return;
    }

    const offsetTop = parentScrollEl.offset().top - scrollEl.offset().top;
    const offsetBottom = offsetTop + parentScrollElHeight;

    if (offsetTop > arrowLocationPixels) {
      scheduleOnce('afterRender', this, function() {
        parentScrollEl.scrollTop(arrowLocationPixels);
      });
    }

    if (arrowLocationPixels >= offsetBottom) {
      scheduleOnce('afterRender', this, function() {
        parentScrollEl.scrollTop(arrowLocationPixels - parentScrollElHeight + optionHeight);
      });
    }
  }),

  handleEnterClicked: action(function(dropdown) {
    const currentlySelectedArrowIndex = this._arrowKeySelectedIndex;
    if (currentlySelectedArrowIndex >= 0) {
      const selectedOption = this._flattenedOptions.objectAt(currentlySelectedArrowIndex);
      const groupedRegex = new RegExp(`^${GROUPED_OPTION_VALUE_SENTINEL}`);

      if (currentlySelectedArrowIndex === 0 && this.defaultOption) {
        this.onDefaultOptionSelected(dropdown);
        return;
      }

      if (!groupedRegex.test(selectedOption.value)) {
        this._addSelectedOption(dropdown, selectedOption);
      }

      return;
    }

    if (!this.selectedOptions.length) {
      dropdown.actions.open();
    }
  }),

  actions: {
    onDefaultOptionSelected(dropdown) {
      this.set('_arrowKeySelectedIndex', -1);
      this.onOptionsCleared();
      dropdown.actions.close();
    },

    /**
     * @param {Dropdown} dropdown
     * @param {MouseEvent} event
     */
    handleKeyDown(dropdown, event) {
      let key = event.which || event.keyCode || 0;
      if (key === KEY_CODES.ENTER) {
        stopClickEvent(event);
        this.handleEnterClicked(dropdown);
        return;
      }

      if (key === KEY_CODES.ESC) {
        stopClickEvent(event);
        this.set('_arrowKeySelectedIndex', -1);
        return;
      }

      if (KEY_CODES__ARROWS_UP_AND_DOWN.includes(key)) {
        stopClickEvent(event);
        this.handleArrowNavigation(key);
        return;
      }
    },

    /**
     * Called when user clicks inside the 'chips' container
     * @param {Dropdown}
     * @param e
     */
    onChipsClicked: function(dropdown, e) {
      if ($(e.target).hasClass('close')) {
        //this is a close('x') click, so don't do anything.
        return;
      }

      scheduleOnce('afterRender', this, function() {
        // focus the input to allow arrow keys after opening.
        $(this.element).find('input').focus();
      });

      dropdown.actions.open();
    },

    /**
     * @param {Object} optionRemoved
     */
    onChipClosedClicked(optionRemoved) {
      this.onOptionRemoved(optionRemoved);
    },

    /**
     * Called after the dropdown has opened.
     */
    onDropdownOpened: function() {
      scheduleOnce('afterRender', this, function() {
        // focus the input to allow arrow keys after opening.
        $(this.element).find('input').focus();
      });
    },

    /**
     *
     * @param {Dropdown} dropdown
     * @param {Object} optionSelected
     */
    onDropdownSelected: function(dropdown, optionSelected) {
      this._addSelectedOption(dropdown, optionSelected);
    },

    /**
     * When the input of the text input has changed.
     */
    onInputTextChanged: function() {
      this._inputTextHandler();
    }
  }
});
